import { useSyncExternalStore } from 'react';
import {
  useSessionIdle, formatSessionTime, SESSION_IDLE_MS,
} from '../../../lib/session-idle';
import { tLayout, getLayoutLang, subscribeLayoutLang } from '../../../lib/layout-i18n';
import { useScreenLockStore } from '../../../store/screenLockStore';
import './SessionWatch.css';

/**
 * SessionWatch (session-watch-rightrail — 2026-07-24) — 우측 사이드 레일의 세션 유지 시계.
 * **프로필 사진과 분리된 독립 요소**로 사진 아래에 놓인다(2026-07-24 사용자 요청 — 아바타
 * 외곽 링 방식은 되돌림). 좁은 레일(64px)에 맞춘 세로 컴팩트 표현(스탑워치 + mm:ss).
 *
 * 표시 전용이다 — 세션 만료 판정·자동 로그아웃은 `lib/session-idle` 싱글턴 엔진이 담당하며,
 * 레일이 없는 화면에서도 동작하도록 AppLayout 이 SessionIdleGuard 로 상시 구독한다.
 * 스탑워치 SVG·상태 임계값·문구는 기존 좌측 바 타이머(v2.9.3)와 동일.
 */
export function SessionWatch({ className }: { className?: string }) {
  useSyncExternalStore(subscribeLayoutLang, getLayoutLang, getLayoutLang); // lang 변경 리렌더
  const { remaining, pct, state } = useSessionIdle();
  const time = formatSessionTime(remaining);
  // 잠금이 켜져 있으면 0 은 로그아웃이 아니라 화면잠금이다 — 툴팁이 그렇게 말해야 한다.
  const lockOn = useScreenLockStore((s) => s.settings.enabled);

  return (
    <div
      className={`session-watch session-watch--${state}${className ? ` ${className}` : ''}`}
      title={tLayout(lockOn ? 'layout.session.title_lock' : 'layout.session.title', { time })}
    >
      <span className="session-watch__icon" aria-hidden>
        <SessionStopwatch pct={pct} sec={remaining} />
      </span>
      <span className="session-watch__time" role="timer" aria-live="off">
        {state === 'expired' ? tLayout('layout.session.expired') : time}
      </span>
    </div>
  );
}

/**
 * 세션 엔진 상시 구독용 headless 컴포넌트 — 렌더 결과 없음.
 * 시계를 그리지 않는 화면에서도 60분 idle 자동 로그아웃이 동작하도록 AppLayout 이 마운트한다.
 * (기존에는 좌측 NavBar 의 SessionTimer 가 그 역할을 겸했다)
 */
export function SessionIdleGuard(): null {
  useSessionIdle();
  return null;
}

/**
 * 스탑워치 아이콘 (v2.9.3 좌측 바 타이머에서 이관 — 그림·모션 규칙 동일).
 *   - 초침: 경과초 × 6° 연속 증가(1분당 1회전) — mod 미사용으로 역방향 전이 없음.
 *   - 외곽 게이지 링: 잔여율(pct) 기반 stroke-dashoffset — 60분에 걸쳐 소진.
 *   - 색은 currentColor → 상태색(ok/warn/danger) 규칙을 그대로 상속.
 */
export function SessionStopwatch({ pct, sec, size = 21 }: { pct: number; sec: number; size?: number }) {
  const elapsed = Math.max(0, SESSION_IDLE_MS / 1000 - sec);
  const handDeg = elapsed * 6;
  const R = 7;
  const C = 2 * Math.PI * R;
  return (
    <svg
      className="session-watch__svg"
      viewBox="0 0 24 26"
      width={size}
      height={Math.round((size * 23) / 21)}
      aria-hidden
    >
      {/* 크라운(상단 버튼) + 우상단 어깨 버튼 */}
      <rect x="10.6" y="0.6" width="2.8" height="3.2" rx="0.9" fill="currentColor" />
      <rect x="17.6" y="2.4" width="2.4" height="2.4" rx="0.8" fill="currentColor" transform="rotate(45 18.8 3.6)" />
      {/* 본체 외곽 */}
      <circle cx="12" cy="15" r="10" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.32" />
      {/* 잔여 게이지 링 (12시 시작, 시계방향 소진) */}
      <circle
        className="session-watch__ring"
        cx="12" cy="15" r={R}
        fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
        strokeDasharray={C}
        strokeDashoffset={C * (1 - pct / 100)}
        transform="rotate(-90 12 15)"
      />
      {/* 초침 + 중심축 */}
      <g className="session-watch__hand" style={{ transform: `rotate(${handDeg}deg)` }}>
        <line x1="12" y1="15" x2="12" y2="10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </g>
      <circle cx="12" cy="15" r="1.2" fill="currentColor" />
    </svg>
  );
}
